import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Paperclip,
  Phone, Video, Bell, Users,
  Minimize2, Image, FileText, Gift
} from 'lucide-react';
import { MinimalLoader } from '../ui/MinimalLoader';
import { useAuth } from '../../contexts/AuthContext';
import './msn-messenger.css';

// Professional color palette for initials
const AVATAR_COLORS = [
  '#6366f1', '#8b5cf6', '#0ea5e9', '#14b8a6', '#10b981',
  '#f59e0b', '#ef4444', '#ec4899', '#3b82f6', '#6b7280',
];

const _getAvatarColor = (name: string) => {
  if (!name) return AVATAR_COLORS[0];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const _getInitials = (name: string) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot' | 'employee';
  senderName?: string;
  senderAvatar?: string;
  timestamp: Date;
  typing?: boolean;
  type?: 'text' | 'emoji' | 'file' | 'image' | 'nudge';
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
}

interface Contact {
  id: string;
  name: string;
  avatar: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  statusMessage?: string;
  department?: string;
  unreadCount?: number;
}

interface ChatSession {
  id: string;
  contactId: string;
  contactName: string;
  messages: Message[];
  isTyping?: boolean;
}

const MSNMessenger: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(true); // Fixed: Now starts open
  const [isMinimized, setIsMinimized] = useState(true);
  const [activeTab, setActiveTab] = useState('ai');
  const [showContacts, setShowContacts] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [nudgeAnimation, setNudgeAnimation] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Draggable position state
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('octa-messenger-position');
    return saved ? JSON.parse(saved) : { x: 0, y: 0 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, originX: 0, originY: 0, moved: false });

  // Button position state
  const [btnPosition, setBtnPosition] = useState(() => {
    const saved = localStorage.getItem('octa-btn-position');
    return saved ? JSON.parse(saved) : { x: 0, y: 0 };
  });
  const [isBtnDragging, setIsBtnDragging] = useState(false);
  const btnDragRef = useRef({ startX: 0, startY: 0, originX: 0, originY: 0, moved: false });

  // API Base URL and Company ID from auth context
  const API_BASE_URL = '/api/v1';
  const COMPANY_ID = user?.company_id || '';
  const USER_ID = user?.id || '';

  // Chat sessions state
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([
    {
      id: 'ai',
      contactId: 'ai-bot',
      contactName: 'OctoAI',
      messages: [
        {
          id: '1',
          text: '👋 Hallo! Ich bin OctoAI, dein CRM-Assistent. Ich kann dir helfen bei:\n\n• 📅 Termine erstellen und verwalten\n• ✅ Aufgaben erstellen und anzeigen\n• 👤 Kandidaten & Kontakte suchen\n• 📝 Notizen erstellen\n• 📊 Tagesübersicht anzeigen\n\nWie kann ich dir helfen?',
          sender: 'bot',
          timestamp: new Date()
        }
      ]
    }
  ]);

  // Team contacts - loaded from API based on company
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(true);

  // Load team members from API
  useEffect(() => {
    const loadTeamMembers = async () => {
      try {
        setContactsLoading(true);
        const response = await fetch(`${API_BASE_URL}/users?company_id=${COMPANY_ID}`, {
          credentials: 'include',
        });
        const data = await response.json();

        if (data.success && data.data) {
          // Map API users to Contact format
          const teamContacts: Contact[] = data.data
            .filter((user: any) => user.status === 'active')
            .map((user: any) => ({
              id: user.id,
              name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
              avatar: '', // No more external avatars - using initials only
              status: (user.online_status as 'online' | 'away' | 'busy' | 'offline') || 'offline',
              statusMessage: user.position || user.department || '',
              department: user.department || '',
              unreadCount: 0
            }));

          setContacts(teamContacts);
        }
      } catch (error) {
        console.error('Error loading team members:', error);
        // Keep empty array on error
      } finally {
        setContactsLoading(false);
      }
    };

    loadTeamMembers();
  }, [API_BASE_URL, COMPANY_ID]);

  // Quick emojis for easy access
  const quickEmojis = ['😊', '👍', '❤️', '😂', '😎', '🎉', '🤔', '👋', '🔥', '💯'];

  // -- Messenger window drag --
  const handleWindowDragStart = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    const ref = dragRef.current;
    ref.startX = e.clientX; ref.startY = e.clientY;
    ref.originX = position.x; ref.originY = position.y;
    ref.moved = false;
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const ref = dragRef.current;
      const dx = e.clientX - ref.startX;
      const dy = e.clientY - ref.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) ref.moved = true;
      setPosition({ x: ref.originX + dx, y: ref.originY + dy });
    };
    const onUp = () => {
      setIsDragging(false);
      setPosition(p => { localStorage.setItem('octa-messenger-position', JSON.stringify(p)); return p; });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [isDragging]);

  // -- Floating button drag --
  const handleBtnDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const ref = btnDragRef.current;
    ref.startX = e.clientX; ref.startY = e.clientY;
    ref.originX = btnPosition.x; ref.originY = btnPosition.y;
    ref.moved = false;
    setIsBtnDragging(true);
  };

  useEffect(() => {
    if (!isBtnDragging) return;
    const onMove = (e: MouseEvent) => {
      const ref = btnDragRef.current;
      const dx = e.clientX - ref.startX;
      const dy = e.clientY - ref.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) ref.moved = true;
      setBtnPosition({ x: ref.originX + dx, y: ref.originY + dy });
    };
    const onUp = () => {
      setIsBtnDragging(false);
      setBtnPosition(p => { localStorage.setItem('octa-btn-position', JSON.stringify(p)); return p; });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [isBtnDragging]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatSessions]);

  const getCurrentSession = () => {
    return chatSessions.find(session => session.id === activeTab) || chatSessions[0];
  };

  const getCurrentContact = () => {
    if (activeTab === 'ai') {
      return {
        id: 'ai-bot',
        name: 'OctoAI',
        avatar: '🐙',
        status: 'online' as const,
        statusMessage: 'Dein CRM-Assistent mit KI-Power'
      };
    }
    return contacts.find(c => c.id === activeTab.replace('contact-', ''));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      alert('Datei zu groß! Maximale Dateigröße: 5MB');
      return;
    }

    setUploadingFile(true);

    try {
      // In production: Upload to server
      // For now: Create local blob URL
      const fileUrl = URL.createObjectURL(file);

      const fileMessage: Message = {
        id: Date.now().toString(),
        text: `📎 ${file.name}`,
        sender: 'user',
        senderName: 'Sie',
        timestamp: new Date(),
        type: file.type.startsWith('image/') ? 'image' : 'file',
        fileUrl: fileUrl,
        fileName: file.name,
        fileSize: file.size
      };

      const updatedSessions = chatSessions.map(session => {
        if (session.id === activeTab) {
          return {
            ...session,
            messages: [...session.messages, fileMessage]
          };
        }
        return session;
      });

      setChatSessions(updatedSessions);
    } catch (error) {
      console.error('File upload error:', error);
      alert('Fehler beim Hochladen der Datei');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const sendMessage = (text?: string) => {
    const messageText = text || inputValue.trim();
    if (!messageText) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      text: messageText,
      sender: 'user',
      senderName: 'Sie',
      timestamp: new Date(),
      type: 'text'
    };

    const _currentSession = getCurrentSession();
    const updatedSessions = chatSessions.map(session => {
      if (session.id === activeTab) {
        return {
          ...session,
          messages: [...session.messages, newMessage]
        };
      }
      return session;
    });

    setChatSessions(updatedSessions);
    setInputValue('');

    // Real API call for AI chat - Using OctoAI with full CRM tools
    if (activeTab === 'ai') {
      setIsTyping(true);

      // Build message history for context
      const currentMessages = getCurrentSession()?.messages || [];
      const messageHistory = currentMessages
        .filter(m => m.type === 'text' || !m.type)
        .slice(-10) // Last 10 messages for context
        .map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.text
        }));

      fetch(`${API_BASE_URL}/ai/octo-chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [...messageHistory, { role: 'user', content: messageText }],
          companyId: COMPANY_ID,
          userId: USER_ID,
        }),
      })
        .then(res => {
          if (!res.ok) throw new Error(`Server-Fehler: ${res.status}`);
          return res.json();
        })
        .then(data => {
          let responseText = data.success ? data.message : 'Entschuldigung, es gab einen Fehler.';

          // Format tool results if any
          if (data.tool_calls && data.tool_calls.length > 0) {
            const toolInfo = data.tool_calls.map((tc: any) => {
              const toolName = tc.function?.name || tc.name || 'Tool';
              return `✅ ${toolName} ausgeführt`;
            }).join('\n');
            responseText = `${toolInfo}\n\n${responseText}`;
          }

          const botResponse: Message = {
            id: (Date.now() + 1).toString(),
            text: responseText,
            sender: 'bot',
            timestamp: new Date()
          };

          setChatSessions(prev => prev.map(session => {
            if (session.id === 'ai') {
              return {
                ...session,
                messages: [...session.messages, botResponse]
              };
            }
            return session;
          }));
          setIsTyping(false);
        })
        .catch(error => {
          console.error('AI Chat error:', error);
          const errorResponse: Message = {
            id: (Date.now() + 1).toString(),
            text: 'Entschuldigung, es gab einen Fehler. Bitte versuche es erneut.',
            sender: 'bot',
            timestamp: new Date()
          };

          setChatSessions(prev => prev.map(session => {
            if (session.id === 'ai') {
              return {
                ...session,
                messages: [...session.messages, errorResponse]
              };
            }
            return session;
          }));
          setIsTyping(false);
        });
    } else {
      // Simulate employee response
      setTimeout(() => {
        simulateEmployeeResponse(activeTab);
      }, 2000 + Math.random() * 3000);
    }
  };

  const _getBotResponse = (input: string): string => {
    const lowerInput = input.toLowerCase();

    if (lowerInput.includes('kandidat')) {
      return '📊 Ich habe 15 passende Kandidaten für Ihre offenen Stellen gefunden. Die Top 3 haben eine Übereinstimmung von über 90%. Möchten Sie die Details sehen?';
    } else if (lowerInput.includes('interview')) {
      return '📅 Für diese Woche sind noch 5 Interview-Slots verfügbar. Soll ich einen Termin für Sie koordinieren?';
    } else if (lowerInput.includes('hilfe') || lowerInput.includes('help')) {
      return '💡 Ich kann Ihnen bei folgenden Aufgaben helfen:\n• Kandidatensuche\n• Interview-Planung\n• Lebenslauf-Analyse\n• E-Mail-Entwürfe\n• Mitarbeiter-Kommunikation\n\nWas möchten Sie tun?';
    } else {
      return '✨ Gerne helfe ich Ihnen dabei. Können Sie mir mehr Details geben, damit ich Sie bestmöglich unterstützen kann?';
    }
  };

  const simulateEmployeeResponse = (sessionId: string) => {
    const responses = [
      'Klar, ich schaue mir das gleich an!',
      'Danke für die Info! 👍',
      'Bin gleich bei dir.',
      'Super, das klingt gut!',
      'Okay, mache ich!',
      'Verstanden, wird erledigt.'
    ];

    const randomResponse = responses[Math.floor(Math.random() * responses.length)];
    const contact = contacts.find(c => `contact-${c.id}` === sessionId);

    if (contact) {
      const employeeMessage: Message = {
        id: Date.now().toString(),
        text: randomResponse,
        sender: 'employee',
        senderName: contact.name,
        senderAvatar: contact.avatar,
        timestamp: new Date()
      };

      setChatSessions(prev => prev.map(session => {
        if (session.id === sessionId) {
          return {
            ...session,
            messages: [...session.messages, employeeMessage]
          };
        }
        return session;
      }));
    }
  };

  const sendNudge = () => {
    setNudgeAnimation(true);

    const nudgeMessage: Message = {
      id: Date.now().toString(),
      text: '🔔 Hat dir einen Stupser gesendet!',
      sender: 'user',
      timestamp: new Date(),
      type: 'nudge'
    };

    const _currentSession = getCurrentSession();
    const updatedSessions = chatSessions.map(session => {
      if (session.id === activeTab) {
        return {
          ...session,
          messages: [...session.messages, nudgeMessage]
        };
      }
      return session;
    });

    setChatSessions(updatedSessions);

    // Play sound effect (simulated)
    setTimeout(() => {
      setNudgeAnimation(false);
    }, 500);
  };

  const sendEmoji = (emoji: string) => {
    sendMessage(emoji);
  };

  const openChatWithContact = (contact: Contact) => {
    const sessionId = `contact-${contact.id}`;

    // Check if session already exists
    const existingSession = chatSessions.find(s => s.id === sessionId);

    if (!existingSession) {
      const newSession: ChatSession = {
        id: sessionId,
        contactId: contact.id,
        contactName: contact.name,
        messages: [
          {
            id: '1',
            text: `Chat mit ${contact.name} gestartet`,
            sender: 'bot',
            timestamp: new Date()
          }
        ]
      };

      setChatSessions([...chatSessions, newSession]);
    }

    setActiveTab(sessionId);
    setShowContacts(false);
  };

  const closeTab = (sessionId: string) => {
    if (sessionId === 'ai') return; // Can't close AI tab

    setChatSessions(prev => prev.filter(s => s.id !== sessionId));

    if (activeTab === sessionId) {
      setActiveTab('ai');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Click outside to minimize
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const messenger = document.querySelector('.msn-messenger-container');
      if (messenger && !messenger.contains(e.target as Node) && !isMinimized) {
        setIsMinimized(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMinimized]);

  return (
    <>
      {/* Floating Button - Always visible when minimized */}
      {isMinimized && (
        <motion.button
          className="btn btn-primary"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            background: 'transparent',
            boxShadow: 'none',
            border: 'none',
            cursor: isBtnDragging ? 'grabbing' : 'pointer',
            padding: 0,
            userSelect: 'none',
            transform: `translate(${btnPosition.x}px, ${btnPosition.y}px)`,
            transition: isBtnDragging ? 'none' : 'transform 0.2s ease-out',
          }}
          onMouseDown={handleBtnDragStart}
          onClick={() => {
            if (!btnDragRef.current.moved) {
              setIsOpen(true);
              setIsMinimized(false);
            }
          }}
          whileHover={{ scale: isBtnDragging ? 1 : 1.1 }}
          whileTap={{ scale: isBtnDragging ? 1 : 0.9 }}
        >
          <MinimalLoader size={0.42} speed={2} label="KI-Assistent öffnen" />
        </motion.button>
      )}

      {/* MSN Messenger Window - Always rendered */}
      <AnimatePresence>
        {isOpen && !isMinimized && (
          <>
            {/* Backdrop — pointerEvents none so it doesn't block input */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9998,
                pointerEvents: 'none'
              }}
            />
            <motion.div
              className={`msn-messenger-container ${nudgeAnimation ? 'messenger-nudge' : ''}`}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
                height: '600px'
              }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              style={{
                pointerEvents: 'auto',
                transform: `translate(${position.x}px, ${position.y}px)`,
                transition: isDragging ? 'none' : undefined,
              }}
              onClick={(e) => e.stopPropagation()}
              onWheel={(e) => e.stopPropagation()}
            >
            {/* Header — drag handle */}
            <div
              className="messenger-header"
              onMouseDown={handleWindowDragStart}
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
              <div className="messenger-title">
                <div style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MinimalLoader size={0.12} speed={2} label="" />
                </div>
                <span style={{ color: 'white' }}>OCTA Messenger</span>
              </div>
              <div className="messenger-controls">
                <button
                  className="control-btn"
                  onClick={() => setIsMinimized(true)}
                  title="Minimieren"
                >
                  <Minimize2 size={14} />
                </button>
              </div>
            </div>

            {/* Chat Tabs */}
            <div className="chat-tabs">
                  {chatSessions.map(session => {
                    const contact = session.id === 'ai'
                      ? { name: 'KI-Assistent', status: 'online' as const }
                      : contacts.find(c => c.id === session.contactId);

                    return (
                      <button
                        key={session.id}
                        className={`chat-tab ${activeTab === session.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(session.id)}
                      >
                        <div className={`tab-status-dot ${contact?.status || 'online'}`} />
                        {session.contactName}
                        {session.id !== 'ai' && (
                          <span
                            className="tab-close"
                            onClick={(e) => {
                              e.stopPropagation();
                              closeTab(session.id);
                            }}
                          >
                            ×
                          </span>
                        )}
                      </button>
                    );
                  })}
                  <button
                    className="chat-tab"
                    onClick={() => setShowContacts(!showContacts)}
                  >
                    <Users size={14} />
                    <span>Kontakte</span>
                  </button>
                </div>

                {/* Chat Content */}
                <div className="chat-content">
                  {/* Contact Info Bar */}
                  <div className="contact-info-bar">
                    <div className="contact-info">
                      {activeTab === 'ai' ? (
                        <>
                          <div className="contact-avatar ai-avatar" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            fontSize: '18px',
                            border: '2px solid rgba(255, 255, 255, 0.8)',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.6), inset 0 0 20px rgba(255, 255, 255, 0.2)'
                          }}>
                            🐙
                          </div>
                          <div className="contact-details">
                            <div className="contact-name">OctoAI</div>
                            <div className="contact-status">Dein CRM-Assistent mit KI-Power</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <img
                            src={getCurrentContact()?.avatar}
                            alt={getCurrentContact()?.name}
                            className="contact-avatar"
                          />
                          <div className="contact-details">
                            <div className="contact-name">{getCurrentContact()?.name}</div>
                            <div className="contact-status">{getCurrentContact()?.statusMessage}</div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="chat-actions">
                      <button className="action-btn" title="Sprachanruf">
                        <Phone size={16} />
                      </button>
                      <button className="action-btn" title="Videoanruf">
                        <Video size={16} />
                      </button>
                      <button
                        className="action-btn nudge-btn"
                        title="Stupser senden"
                        onClick={sendNudge}
                      >
                        <Bell size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="messages-container">
                    {getCurrentSession()?.messages.map((message) => (
                      <div
                        key={message.id}
                        className={`message ${message.sender === 'user' ? 'sent' : 'received'}`}
                      >
                        {message.sender !== 'user' && (
                          message.sender === 'bot' ? (
                            <div className="message-avatar ai-avatar" style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'linear-gradient(135deg, #667eea, #764ba2)',
                              fontSize: '14px',
                              border: '2px solid rgba(255, 255, 255, 0.8)',
                              boxShadow: '0 2px 6px rgba(102, 126, 234, 0.5), inset 0 0 15px rgba(255, 255, 255, 0.2)'
                            }}>
                              🐙
                            </div>
                          ) : (
                            <img
                              src={message.senderAvatar}
                              alt={message.senderName}
                              className="message-avatar"
                            />
                          )
                        )}
                        <div className="message-bubble">
                          {message.type === 'nudge' ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Bell size={16} />
                              <span className="message-text">{message.text}</span>
                            </div>
                          ) : message.type === 'image' && message.fileUrl ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <img
                                src={message.fileUrl}
                                alt={message.fileName}
                                style={{
                                  maxWidth: '250px',
                                  maxHeight: '250px',
                                  borderRadius: '8px',
                                  cursor: 'pointer'
                                }}
                                onClick={() => window.open(message.fileUrl, '_blank')}
                              />
                              <div className="message-text" style={{ fontSize: '12px', opacity: 0.8 }}>
                                {message.fileName} ({(message.fileSize! / 1024).toFixed(0)} KB)
                              </div>
                            </div>
                          ) : message.type === 'file' && message.fileUrl ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FileText size={20} />
                                <div>
                                  <div className="message-text">{message.fileName}</div>
                                  <div style={{ fontSize: '11px', opacity: 0.7 }}>
                                    {(message.fileSize! / 1024).toFixed(0)} KB
                                  </div>
                                </div>
                              </div>
                              <a
                                href={message.fileUrl}
                                download={message.fileName}
                                style={{
                                  color: '#667eea',
                                  textDecoration: 'none',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                📥 Herunterladen
                              </a>
                            </div>
                          ) : (
                            <div className="message-text">{message.text}</div>
                          )}
                          <div className="message-time">
                            {message.timestamp.toLocaleTimeString('de-DE', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                    ))}

                    {isTyping && (
                      <div className="message">
                        <div className="message-avatar ai-avatar" style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          fontSize: '14px',
                          border: '2px solid rgba(255, 255, 255, 0.8)',
                          boxShadow: '0 2px 6px rgba(102, 126, 234, 0.5), inset 0 0 15px rgba(255, 255, 255, 0.2)'
                        }}>
                          🐙
                        </div>
                        <div className="typing-indicator">
                          <div className="typing-dots">
                            <div className="typing-dot" />
                            <div className="typing-dot" />
                            <div className="typing-dot" />
                          </div>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input Area */}
                  <div className="messenger-input-area">
                    {/* Emoji Bar */}
                    <div className="emoji-bar">
                      {quickEmojis.map(emoji => (
                        <button
                          key={emoji}
                          className="emoji-btn"
                          onClick={() => sendEmoji(emoji)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>

                    {/* Input */}
                    <div className="input-container" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <textarea
                        className="message-input"
                        placeholder="Nachricht eingeben..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                        rows={1}
                        style={{
                          flex: 1,
                          background: 'rgba(255, 255, 255, 0.15)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '20px',
                          padding: '8px 16px',
                          color: '#fff',
                          fontSize: '14px',
                          resize: 'none',
                          outline: 'none',
                          maxHeight: '100px',
                          caretColor: '#fff',
                        }}
                      />

                      {/* Hidden file input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                        accept="*/*"
                      />

                      <div className="input-actions" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <button
                          className="input-action-btn"
                          title="Datei anhängen (max 5MB)"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFile}
                        >
                          <Paperclip size={18} />
                        </button>
                        <button
                          className="input-action-btn"
                          title="Bild senden"
                          onClick={() => {
                            fileInputRef.current?.click();
                          }}
                        >
                          <Image size={18} />
                        </button>
                        <button className="input-action-btn" title="Geschenk senden">
                          <Gift size={18} />
                        </button>
                        <button
                          className="input-action-btn send"
                          onClick={() => sendMessage()}
                          disabled={!inputValue.trim()}
                          style={{
                            background: inputValue.trim() ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'rgba(255, 255, 255, 0.1)',
                            opacity: inputValue.trim() ? 1 : 0.5
                          }}
                        >
                          <Send size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Sidebar */}
                <div className={`contact-sidebar ${showContacts ? 'open' : ''}`}>
                  <div className="contact-list">
                    <div style={{
                      padding: '8px',
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.6)',
                      borderBottom: '1px solid rgba(255,255,255,0.1)'
                    }}>
                      {contactsLoading ? 'Lade Mitarbeiter...' : `Mitarbeiter (${contacts.length}) - Online: ${contacts.filter(c => c.status === 'online').length}`}
                    </div>
                    {contactsLoading ? (
                      <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '13px'
                      }}>
                        <div style={{ marginBottom: '8px' }}>🔄</div>
                        Lade Teammitglieder...
                      </div>
                    ) : contacts.length === 0 ? (
                      <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '13px'
                      }}>
                        <div style={{ marginBottom: '8px' }}>👥</div>
                        Keine Teammitglieder gefunden.
                        <br />
                        <span style={{ fontSize: '11px', opacity: 0.7 }}>
                          Laden Sie Mitarbeiter über Einstellungen ein.
                        </span>
                      </div>
                    ) : (
                      contacts.map(contact => (
                        <div
                          key={contact.id}
                          className="contact-item"
                          onClick={() => openChatWithContact(contact)}
                        >
                          <div style={{ position: 'relative' }}>
                            <img
                              src={contact.avatar}
                              alt={contact.name}
                              className="contact-item-avatar"
                            />
                            <div className={`contact-item-status ${contact.status}`}
                              style={{
                                background: contact.status === 'online' ? '#10b981' :
                                          contact.status === 'away' ? '#fbbf24' :
                                          contact.status === 'busy' ? '#ef4444' : '#6b7280'
                              }}
                            />
                          </div>
                          <span className="contact-item-name">{contact.name}</span>
                          {contact.unreadCount ? (
                            <span style={{
                              background: '#ef4444',
                              color: '#fff',
                              borderRadius: '10px',
                              padding: '2px 6px',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}>
                              {contact.unreadCount}
                            </span>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MSNMessenger;